import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import {
  MeasurementSessionStatus,
  TERMINAL_SESSION_STATUSES,
  type MeasurementQrCode,
  type MeasurementSessionStatus as SessionStatus,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useLocale } from '@/hooks/useLocale';
import { apiErrorMessage } from '@/services/apiClient';
import { formatDateTime } from '@/utils/format';
import {
  useCancelMeasurementSessionMutation,
  useCreateMeasurementSessionMutation,
  useIssueQrCodeMutation,
  useMeasurementSessionQuery,
} from '../api/measurements.api';
import { useSessionStream } from '../api/useSessionStream';
import { CameraPdReview } from './CameraPdReview';

/** How often the desk asks where the phone has got to, if the status stream cannot be kept open. */
const POLL_MS = 3_000;

/**
 * Hands a measurement to a phone through a one-time code.
 *
 * The code exists in this dialog's memory and in the QR it draws, nowhere else. Closing the dialog
 * ends any session still open, so a phone is never left holding a capability nobody is watching.
 */
export function PhoneHandoverDialog({
  customerId,
  isOpen,
  onClose,
}: {
  customerId: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const create = useCreateMeasurementSessionMutation();
  const issue = useIssueQrCodeMutation();
  const cancel = useCancelMeasurementSessionMutation();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [code, setCode] = useState<MeasurementQrCode | null>(null);
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  const lastStatus = useRef<SessionStatus | null>(null);
  const live = isOpen && !isEnded(lastStatus.current);
  // The stream prompts a refetch on every change; polling steps in only if the stream cannot stay open.
  const transport = useSessionStream(sessionId, live);
  const session = useMeasurementSessionQuery(
    sessionId,
    live && transport === 'polling' ? POLL_MS : false,
  );
  const status = session.data?.status ?? code?.session.status ?? null;
  lastStatus.current = status;

  const requestCode = (id: string) => {
    setError(null);
    issue.mutate(id, {
      onSuccess: async (issued) => {
        setCode(issued);
        setQrImage(await QRCode.toDataURL(issued.qrUrl, { margin: 1, width: 240 }));
      },
      onError: (caught) => setError(apiErrorMessage(caught) ?? t('measurements.phone.errors.code')),
    });
  };

  useEffect(() => {
    // Effects run twice in development; one dialog opening is one session.
    if (!isOpen || started.current) return;
    started.current = true;

    create.mutate(
      { customerId, requestedSource: 'PHONE_QR' },
      {
        onSuccess: (created) => {
          setSessionId(created.id);
          requestCode(created.id);
        },
        onError: (caught) =>
          setError(apiErrorMessage(caught) ?? t('measurements.phone.errors.start')),
      },
    );
    // The mutations are stable for the dialog's life; only opening it should start a session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, customerId]);

  const close = () => {
    if (sessionId && status && !isEnded(status)) cancel.mutate(sessionId);
    started.current = false;
    setSessionId(null);
    setCode(null);
    setQrImage(null);
    setError(null);
    onClose();
  };

  const waiting = status === MeasurementSessionStatus.WAITING_FOR_DEVICE;
  const underReview =
    status === MeasurementSessionStatus.AWAITING_REVIEW && session.data?.candidate
      ? session.data
      : null;
  const codeExpired = Boolean(code && waiting && Date.now() > Date.parse(code.tokenExpiresAt));
  const isValidation = session.data?.isValidation ?? code?.session.isValidation ?? false;

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={t('measurements.phone.title')}
      description={t('measurements.phone.description')}
      footer={
        <>
          {waiting ? (
            <Button
              variant="secondary"
              onClick={() => sessionId && requestCode(sessionId)}
              isLoading={issue.isPending}
            >
              {t('measurements.phone.newCode')}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={close}>
            {status && !isEnded(status) ? t('measurements.phone.cancel') : t('common.close')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="error">{error}</Alert> : null}
        {isValidation ? <Alert tone="warning">{t('measurements.phone.validation')}</Alert> : null}

        {waiting && qrImage && !codeExpired ? (
          <figure className="flex flex-col items-center gap-3">
            <img
              src={qrImage}
              alt={t('measurements.phone.qrAlt')}
              width={240}
              height={240}
              className="rounded-md border border-line bg-white p-2"
            />
            {code ? (
              <figcaption className="text-muted text-xs">
                {t('measurements.phone.validUntil', {
                  time: formatDateTime(code.tokenExpiresAt, locale),
                })}
              </figcaption>
            ) : null}
          </figure>
        ) : null}

        <p className="text-ink text-sm font-medium" role="status" aria-live="polite">
          {codeExpired ? t('measurements.phone.codeExpired') : t(statusMessage(status))}
        </p>

        {underReview ? (
          <CameraPdReview session={underReview} customerId={customerId} onDone={close} />
        ) : null}

        {status && !isEnded(status) ? (
          <p className="text-muted text-xs">{t('measurements.phone.closeEnds')}</p>
        ) : null}
      </div>
    </Modal>
  );
}

function isEnded(status: SessionStatus | null): boolean {
  return status !== null && TERMINAL_SESSION_STATUSES.includes(status);
}

function statusMessage(status: SessionStatus | null): string {
  switch (status) {
    case null:
    case MeasurementSessionStatus.CREATED:
      return 'measurements.phone.status.preparing';
    case MeasurementSessionStatus.WAITING_FOR_DEVICE:
      return 'measurements.phone.status.waiting';
    case MeasurementSessionStatus.CAPTURING:
      return 'measurements.phone.status.capturing';
    case MeasurementSessionStatus.AWAITING_REVIEW:
      return 'measurements.phone.status.review';
    case MeasurementSessionStatus.COMPLETED:
      return 'measurements.phone.status.completed';
    case MeasurementSessionStatus.CANCELLED:
      return 'measurements.phone.status.cancelled';
    case MeasurementSessionStatus.EXPIRED:
      return 'measurements.phone.status.expired';
    case MeasurementSessionStatus.FAILED:
      return 'measurements.phone.status.failed';
  }
}
